import type { FieldDefinitionType } from '#root/components/dynamicForm/types/field.type.ts';
import { useLanguage } from '#root/composables/useLanguage.ts';
import type { CardCheckBoxPropsType, InputSearchModelType } from '@c4-links/vue3-links-components';
import { FilterDefinitionFormType, type FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { computed, ref, watch, type Ref } from 'vue';

type CheckboxCardSelectionType = {
  [key: string]: boolean;
};

/**
 * composable for orchestrating checkbox card filter logic
 */
export const useCheckboxCardSelection = ({
  field,
  searchData,
  searchValue,
  initialValue,
  onSelectionChange
}: {
  initialValue: Ref<unknown>;
  field: FieldDefinitionType;
  searchValue: Ref<InputSearchModelType | undefined>;
  searchData: Readonly<Ref<FilterDefinitionValue[] | undefined>>;
  onSelectionChange: (selections: string[]) => void;
}) => {
  const { t } = useLanguage();

  const checkBoxOptions = ref<FilterDefinitionValue[]>(field.options ?? []);

  const nbSelectedCheckboxItems = computed(() => Object.values(modelValue.value).filter(Boolean).length);
  const checkboxCardValues = computed<(CardCheckBoxPropsType & { label: string; value: string })[]>(() => {
    if (field.element === FilterDefinitionFormType.CHECKBOX_CARD) {
      return (
        checkBoxOptions.value.map(
          (option) =>
            ({
              label: option.label,
              value: option.value,
              id: `checkbox-group-${option.label}`,
              title: t(`filters.form.value.${option.label}`),
              icon: t(`filters.form.value.icon.${option.label}`),
              description: t(`filters.form.value.description.${option.label}`)
            }) as CardCheckBoxPropsType & { label: string; value: string }
        ) ?? []
      );
    }

    return [];
  });

  const modelValue = ref<CheckboxCardSelectionType>({});

  // ———— B ———— Functions ————————————————————————————————————————————————————————————————————————————————

  const initialize = () => {
    if (initialValue.value) {
      const raw = initialValue.value as string[];

      raw.forEach((value) => {
        modelValue.value[value] = true;
      });
    } else {
      checkboxCardValues.value.forEach((option) => {
        modelValue.value[option.value] = false;
      });
    }
  };

  const selectAll = () => {
    checkboxCardValues.value.forEach((option) => {
      modelValue.value[option.value] = true;
    });

    onSelectionChange(checkboxCardValues.value.map((option) => option.value));
  };

  const deselectAll = () => {
    checkboxCardValues.value.forEach((option) => {
      modelValue.value[option.value] = false;
    });

    onSelectionChange([]);
  };

  // ———— E ———— Functions ————————————————————————————————————————————————————————————————————————————————

  // ———— B ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  /**
   * Rebuilds the checkbox options when search results are available.
   */
  watch(
    [searchData, searchValue],
    ([newData, newSearchValue]) => {
      if (
        newData &&
        newData.length > 0 &&
        (newSearchValue?.searchValue !== '' || newSearchValue?.searchValue.length > 0)
      ) {
        checkBoxOptions.value = newData;
      } else if (
        ((newData && newData.length === 0) || !newData) &&
        (newSearchValue?.searchValue === '' || !newSearchValue?.searchValue || newSearchValue?.searchValue.length === 0)
      ) {
        checkBoxOptions.value = field.options ?? [];
      } else if (newData && newData.length === 0 && newSearchValue?.searchValue) {
        checkBoxOptions.value = [];
      }
    },
    { deep: true }
  );

  watch(modelValue.value, (newValue) => {
    const selectedValues = Object.entries(newValue)
      .filter(([_, isSelected]) => isSelected)
      .map(([value]) => value);

    onSelectionChange(selectedValues);
  });

  // ———— E ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  return {
    modelValue,
    checkboxCardValues,
    nbSelectedCheckboxItems,

    selectAll,
    initialize,
    deselectAll
  };
};
